import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { RecipeWithDetails, RecipeIngredient, RecipeStep } from "@/models/cookbook";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Minus, Plus, ArrowLeft, Users, ChevronDown, ChevronUp, Clock, ChefHat, Utensils, Lightbulb, PlusCircle, ExternalLink } from "lucide-react";
import { AppMenu } from "@/components/navigation/AppMenu";
import { LanguageSelector } from "@/components/decision/LanguageSelector";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";

const RecipeDetail = () => {
    const { id } = useParams<{ id: string }>();
    const [recipe, setRecipe] = useState<RecipeWithDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [portions, setPortions] = useState(4);
    const [showIngredients, setShowIngredients] = useState(true);

    // Tip Dialog State
    const [isTipDialogOpen, setIsTipDialogOpen] = useState(false);
    const [currentStepId, setCurrentStepId] = useState<string | null>(null);
    const [tipContent, setTipContent] = useState("");
    const { toast } = useToast();

    useEffect(() => {
        const fetchRecipe = async () => {
            if (!id) return;

            try {
                // Fetch recipe
                const { data: recipeData, error: recipeError } = await supabase
                    .from("recipes")
                    .select("*")
                    .eq("id", id)
                    .single();

                if (recipeError) throw recipeError;

                // Fetch ingredients
                const { data: ingredientsData, error: ingredientsError } = await supabase
                    .from("recipe_ingredients")
                    .select("*")
                    .eq("recipe_id", id)
                    .order("order_index", { ascending: true });

                if (ingredientsError) throw ingredientsError;

                // Fetch steps
                const { data: stepsData, error: stepsError } = await supabase
                    .from("recipe_steps")
                    .select("*")
                    .eq("recipe_id", id)
                    .order("order_index", { ascending: true });

                if (stepsError) throw stepsError;

                if (recipeData) {
                    setRecipe({
                        ...recipeData,
                        ingredients: ingredientsData || [],
                        steps: stepsData || [],
                    });
                    setPortions(recipeData.servings || 4);
                }
            } catch (error) {
                console.error("Error fetching recipe details:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchRecipe();
    }, [id]);

    const handlePortionChange = (change: number) => {
        setPortions((prev) => Math.max(1, prev + change));
    };

    const calculateQuantity = (ingredient: RecipeIngredient) => {
        if (!ingredient.quantity || !recipe?.servings) return null;
        const baseQuantity = ingredient.quantity / recipe.servings;
        const newQuantity = baseQuantity * portions;

        // Round to 2 decimal places if needed, or keep integer if it was integer
        return Number.isInteger(newQuantity) ? newQuantity : parseFloat(newQuantity.toFixed(2));
    };

    const getIngredientsForStep = (stepId: string) => {
        return recipe?.ingredients.filter(i => i.step_id === stepId) || [];
    };



    const openTipDialog = (stepId: string, currentRecommendation: string | null) => {
        setCurrentStepId(stepId);
        setTipContent(currentRecommendation || "");
        setIsTipDialogOpen(true);
    };

    const handleSaveTip = async () => {
        if (!currentStepId || !recipe) return;

        try {
            const { error } = await supabase
                .from("recipe_steps")
                .update({ recommendation: tipContent })
                .eq("id", currentStepId);

            if (error) throw error;

            // Update local state
            const updatedSteps = recipe.steps.map(step =>
                step.id === currentStepId ? { ...step, recommendation: tipContent } : step
            );
            setRecipe({ ...recipe, steps: updatedSteps });

            setIsTipDialogOpen(false);
            toast({
                title: "Tip saved",
                description: "The recommendation has been updated.",
            });
        } catch (error) {
            console.error("Error saving tip:", error);
            toast({
                title: "Error",
                description: "Failed to save the tip. Please try again.",
                variant: "destructive",
            });
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background text-foreground p-4 md:p-8">
                <div className="max-w-4xl mx-auto mt-16 space-y-8">
                    <Skeleton className="h-12 w-3/4 mx-auto" />
                    <Skeleton className="h-64 w-full rounded-lg" />
                    <div className="space-y-4">
                        <Skeleton className="h-32 w-full" />
                        <Skeleton className="h-32 w-full" />
                    </div>
                </div>
            </div>
        );
    }

    if (!recipe) {
        return (
            <div className="min-h-screen bg-background text-foreground p-4 flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold mb-4">Recipe not found</h1>
                    <Link to="/cookbook">
                        <Button>Back to Cookbook</Button>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background text-foreground p-4 md:p-8">
            <div className="absolute top-4 right-4 grid grid-cols-2 gap-2 z-10">
                <LanguageSelector />
                <AppMenu />
            </div>

            <div className="max-w-5xl mx-auto mt-8 md:mt-16">
                <Link to="/cookbook" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-6 transition-colors">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Cookbook
                </Link>

                <div className="space-y-8">
                    {/* Header Section */}
                    <div className="text-center space-y-4">
                        <h1 className="text-4xl md:text-5xl font-bold tracking-tight">{recipe.title}</h1>
                        <div className="flex flex-wrap justify-center gap-4 text-muted-foreground mt-2">
                            {recipe.category && (
                                <div className="flex items-center gap-1 bg-secondary/50 px-3 py-1 rounded-full text-sm">
                                    <Utensils className="h-4 w-4" />
                                    <span>{recipe.category}</span>
                                </div>
                            )}
                            {recipe.cooking_time && (
                                <div className="flex items-center gap-1 bg-secondary/50 px-3 py-1 rounded-full text-sm">
                                    <Clock className="h-4 w-4" />
                                    <span>{recipe.cooking_time} min</span>
                                </div>
                            )}
                            {recipe.difficulty && (
                                <div className="flex items-center gap-1 bg-secondary/50 px-3 py-1 rounded-full text-sm">
                                    <ChefHat className="h-4 w-4" />
                                    <span>{recipe.difficulty}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Image Section */}
                    {recipe.image_url && (
                        <div className="rounded-xl overflow-hidden shadow-xl aspect-video w-full max-h-[500px] object-cover">
                            <img
                                src={recipe.image_url}
                                alt={recipe.title}
                                className="w-full h-full object-cover"
                            />
                        </div>
                    )}

                    {recipe.description && (
                        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                            {recipe.description}
                        </p>
                    )}

                    {/* Portions Selector */}
                    <div className="flex justify-center">
                        <div className="flex items-center justify-between bg-background rounded-full p-1 border shadow-sm w-48">
                            <Button
                                variant="ghost"
                                size="icon"
                                className="rounded-full h-10 w-10 hover:bg-muted"
                                onClick={() => handlePortionChange(-1)}
                                disabled={portions <= 1}
                            >
                                <Minus className="h-4 w-4" />
                            </Button>
                            <div className="flex items-center gap-2">
                                <Users className="h-4 w-4 text-muted-foreground" />
                                <span className="text-xl font-bold">{portions}</span>
                            </div>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="rounded-full h-10 w-10 hover:bg-muted"
                                onClick={() => handlePortionChange(1)}
                            >
                                <Plus className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>

                    {/* All Ingredients Summary */}
                    <Card className="border-none shadow-sm bg-secondary/30">
                        <CardContent className="pt-6">
                            <div
                                className="flex items-center justify-between cursor-pointer mb-4"
                                onClick={() => setShowIngredients(!showIngredients)}
                            >
                                <h3 className="font-semibold text-lg">All Ingredients</h3>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                    {showIngredients ? (
                                        <ChevronUp className="h-4 w-4" />
                                    ) : (
                                        <ChevronDown className="h-4 w-4" />
                                    )}
                                </Button>
                            </div>

                            {showIngredients && (
                                <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-2 animate-in fade-in slide-in-from-top-2 duration-200">
                                    {recipe.ingredients.map((ingredient) => (
                                        <li key={ingredient.id} className="flex justify-between items-baseline text-sm md:text-base border-b border-border/50 pb-1">
                                            <span className="font-medium whitespace-nowrap">
                                                {calculateQuantity(ingredient)} {ingredient.unit}
                                            </span>
                                            <span className="text-muted-foreground ml-2 text-right">
                                                {ingredient.name}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </CardContent>
                    </Card>

                    {/* Steps and Ingredients List */}
                    <div className="space-y-8 md:space-y-12">
                        {/* Unlinked Ingredients (if any) */}


                        {/* Steps with Linked Ingredients */}
                        {recipe.steps.map((step, index) => {
                            const stepIngredients = getIngredientsForStep(step.id);
                            return (
                                <div key={step.id} className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 items-start group">

                                    {/* Step Number (Mobile only, or integrated) */}
                                    <div className="md:hidden flex items-center gap-3 mb-2">
                                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground font-bold text-lg shadow-md">
                                            {index + 1}
                                        </div>
                                        <h3 className="font-semibold text-lg">Step {index + 1}</h3>
                                    </div>

                                    {/* Ingredients Column */}
                                    <div className="md:col-span-4 order-2 md:order-1">
                                        {stepIngredients.length > 0 ? (
                                            <div className="bg-secondary/20 rounded-lg p-4 md:p-6">
                                                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider mb-3 md:hidden">Ingredients</h4>
                                                <ul className="space-y-3">
                                                    {stepIngredients.map((ingredient) => (
                                                        <li key={ingredient.id} className="flex justify-between items-baseline text-sm md:text-base">
                                                            <span className="font-bold whitespace-nowrap">
                                                                {calculateQuantity(ingredient)} {ingredient.unit}
                                                            </span>
                                                            <span className="text-foreground/80 text-right ml-3">
                                                                {ingredient.name}
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ) : (
                                            <div className="hidden md:block text-muted-foreground text-sm italic p-4">
                                                No ingredients for this step
                                            </div>
                                        )}
                                    </div>

                                    {/* Description Column */}
                                    <div className="md:col-span-8 order-1 md:order-2">
                                        <div className="flex gap-4 md:gap-6">
                                            <div className="hidden md:flex flex-shrink-0 items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold text-xl shadow-md mt-1">
                                                {index + 1}
                                            </div>
                                            <div className="prose prose-neutral dark:prose-invert max-w-none pt-1 flex-1">
                                                <p className="text-lg leading-relaxed text-foreground">
                                                    {step.description}
                                                </p>
                                                {step.recommendation && (
                                                    <div className="mt-4 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-4 rounded-r-lg relative group/tip">
                                                        <div className="flex items-start gap-3">
                                                            <div className="mt-1">
                                                                <ChefHat className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                                                            </div>
                                                            <div>
                                                                <h4 className="font-semibold text-yellow-800 dark:text-yellow-300 text-sm uppercase tracking-wide mb-1">
                                                                    Chef's Tip
                                                                </h4>
                                                                <p className="text-yellow-700 dark:text-yellow-200/90 italic">
                                                                    {step.recommendation}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="absolute top-2 right-2 h-6 w-6 text-yellow-600 hover:bg-yellow-100 dark:hover:bg-yellow-900/40"
                                                            onClick={() => openTipDialog(step.id, step.recommendation)}
                                                        >
                                                            <PlusCircle className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                )}
                                                {!step.recommendation && (
                                                    <div className="mt-2">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-muted-foreground hover:text-primary gap-2 text-xs h-6 px-2"
                                                            onClick={() => openTipDialog(step.id, null)}
                                                        >
                                                            <Lightbulb className="h-3 w-3" />
                                                            Add Tip
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Source URL */}
                    {recipe.source_url && (
                        <div className="flex justify-center mt-12 pt-8 border-t">
                            <a
                                href={recipe.source_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                            >
                                <ExternalLink className="h-4 w-4" />
                                <span>View Original Recipe</span>
                            </a>
                        </div>
                    )}
                </div>
            </div>

            <Dialog open={isTipDialogOpen} onOpenChange={setIsTipDialogOpen}>
                <DialogContent className="bg-card">
                    <DialogHeader>
                        <DialogTitle>Add Chef's Tip</DialogTitle>
                        <DialogDescription>
                            Add a helpful tip or recommendation for this step.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <Textarea
                            value={tipContent}
                            onChange={(e) => setTipContent(e.target.value)}
                            placeholder="e.g. Make sure the butter is at room temperature..."
                            className="min-h-[100px] bg-card"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsTipDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleSaveTip}>Save Tip</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default RecipeDetail;
